import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table } from '../../ui';
import { Card, Tag, Button, Alert, Space, Typography, Popconfirm, Tooltip, Modal, Input, Spinner, message } from '../../ui';
import { useNavigate } from 'react-router-dom';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';

/**
 * Fulfillment V2 · Phase 1 — read-only warehouse coverage + guided planning for an order's PENDING qty.
 * Shows every warehouse's product-wise coverage (FULL / PARTIAL / NONE), combined feasibility, and a
 * transfer suggestion. Availability is PHYSICAL on-hand (not reservation-aware yet). All ACTION buttons
 * are gated by permission — the backend is the final authority; a view-only approver sees the matrix but
 * no actions. Actions only call EXISTING endpoints (assign dispatch warehouse / consolidate); it never
 * reserves stock, packs, or touches invoice/payment/ledger/advance-CD.
 */
const COVERAGE: Record<string, { color: string; label: string }> = {
  FULL: { color: 'green', label: 'Full' },
  PARTIAL: { color: 'gold', label: 'Partial' },
  NONE: { color: 'default', label: 'None' },
};

export function WarehouseCoveragePanel({ orderId, dispatchWarehouseId, canPlan, canAddStock, canReportShortfall, orderStatus, transfers, onChanged }: {
  orderId: string;
  dispatchWarehouseId?: string | null;
  canPlan: boolean;
  canAddStock: boolean;
  canReportShortfall?: boolean;   // Phase 2 — WHM/packer can escalate a shortfall to the planner
  orderStatus?: string;
  transfers?: any[];              // order's linked transfers (attached when it was AWAITING_TRANSFER)
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isFetching, isLoading, isError } = useQuery({
    queryKey: ['order-coverage', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/warehouse-coverage` }),
    retry: false, // a 403 (no coverage permission) should fail fast, not spin
  });
  const [picked, setPicked] = useState<string | undefined>();
  const [reportOpen, setReportOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  // The OPEN shortfall for this order (if any), so a planner/admin who follows the notification lands on
  // the order and reads the reporter's remark here — not only in the queue page / activity log.
  const canSeeShortfall = !!canReportShortfall || !!canPlan;
  const { data: shortfall } = useQuery({
    queryKey: ['order-shortfall', orderId],
    queryFn: () => request<any>({ url: `/shortfalls/order/${orderId}` }),
    enabled: canSeeShortfall,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['order-coverage', orderId] });
    qc.invalidateQueries({ queryKey: ['order-shortfall', orderId] });
    qc.invalidateQueries({ queryKey: ['order', orderId] });
    qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
    onChanged?.();
  };
  const onErr = (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed');
  const assign = useMutation({
    mutationFn: (wid: string) => request({ url: `/orders/${orderId}/dispatch-warehouse`, method: 'POST', data: { warehouseId: wid } }),
    onSuccess: () => { message.success('Dispatch warehouse assigned'); refresh(); }, onError: onErr,
  });
  const consolidate = useMutation({
    mutationFn: (wid: string) => request({ url: `/orders/${orderId}/consolidate`, method: 'POST', data: { toWarehouseId: wid } }),
    onSuccess: () => { message.success('Consolidating stock — order held until the transfers are received'); refresh(); }, onError: onErr,
  });
  const reportShortfall = useMutation({
    mutationFn: () => request({ url: `/orders/${orderId}/shortfall-report`, method: 'POST', data: { remarks: remarks.trim() || undefined } }),
    onSuccess: () => { message.success('Shortfall reported to the planner'); setReportOpen(false); setRemarks(''); refresh(); }, onError: onErr,
  });

  if (isLoading) return (
    <Card title="Warehouse coverage" style={{ marginBottom: 16 }}>
      <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>
    </Card>
  );
  if (isError || !data) return (
    <Card title="Warehouse coverage" style={{ marginBottom: 16 }}>
      <Alert type="warning" title="Warehouse coverage isn't available for you on this order." />
    </Card>
  );

  const s = data.suggestion ?? {};
  const combined = data.combined ?? {};
  const outcome: string = s.outcome;

  // Phase 2 — derived fulfillment sub-status (no DB field): from order status + linked transfers + coverage.
  const tfs: any[] = transfers ?? [];
  const subStatus: { color: string; text: string } | null = (() => {
    if (orderStatus === 'AWAITING_TRANSFER') {
      if (tfs.some((t) => t.status === 'PARTIAL')) return { color: 'red', text: 'Transfer discrepancy — blocked' };
      return { color: 'gold', text: 'Transfer in progress' };
    }
    if (tfs.length && tfs.some((t) => t.status === 'RECEIVED') && tfs.every((t) => ['RECEIVED', 'CANCELLED'].includes(t.status))) {
      return { color: 'green', text: 'Stock received — ready to pack' };
    }
    if (!data.pending?.fullyPacked && (outcome === 'NEEDS_TRANSFER' || outcome === 'INSUFFICIENT_TOTAL')) {
      return { color: 'orange', text: 'Short stock' };
    }
    return null;
  })();
  const isShort = outcome === 'NEEDS_TRANSFER' || outcome === 'INSUFFICIENT_TOTAL';
  const selected = picked ?? s.suggestedDestinationWarehouseId ?? (dispatchWarehouseId ? String(dispatchWarehouseId) : undefined);
  const selectedRow = (data.warehouses ?? []).find((w: any) => w.warehouseId === selected);

  const flags = (w: any) => (
    <Space size={4} wrap>
      {w.isAssigned && <Tag tone="blue">Assigned</Tag>}
      {!w.isActive && <Tag tone="red">Inactive</Tag>}
      {w.type === 'PRODUCTION' && <Tag>Production</Tag>}
    </Space>
  );

  return (
    <Card style={{ marginBottom: 16 }}
      title={<Space wrap>Warehouse coverage (plan fulfillment){subStatus && <Tag color={subStatus.color}>{subStatus.text}</Tag>}</Space>}>
      <Alert type="info" style={{ marginBottom: 10 }}
        title={data.meta?.note ?? 'Availability is physical stock only. Final packing will re-check stock.'}
        description="Physical on-hand across warehouses — not reserved for this order yet. Coverage is decided per product." />

      {shortfall && (() => {
        const lines = Array.isArray(shortfall.shortSnapshot) ? shortfall.shortSnapshot
          : (typeof shortfall.shortSnapshot === 'string' ? (() => { try { return JSON.parse(shortfall.shortSnapshot); } catch { return []; } })() : []);
        const shortText = lines.length ? lines.map((l: any) => `${fmtQty(l.shortQty)} × ${l.productName ?? l.productId}`).join(', ') : 'short stock';
        return (
          <Alert
            type="warning"
            style={{ marginBottom: 12 }}
            title={<span>Shortfall reported{shortfall.reportedByName ? ` by ${shortfall.reportedByName}` : ''} — short {shortText}</span>}
            description={shortfall.remarks
              ? <span style={{ fontStyle: 'italic' }}>“{shortfall.remarks}”</span>
              : 'No note added — add stock or arrange production for the pending quantity.'}
            action={canAddStock ? <Button size="sm" variant="default" onClick={() => navigate('/inventory/master-stock')}>Add stock</Button> : undefined}
          />
        );
      })()}

      {outcome === 'NO_PENDING' ? (
        <Alert type="success" title="This order is fully packed — nothing left to fulfill." />
      ) : (
        <>
          {/* Combined feasibility banner */}
          <Alert style={{ marginBottom: 12 }} type={combined.canFulfill ? 'success' : 'error'}
            title={combined.canFulfill
              ? `Combined across all warehouses: ${fmtQty(combined.totalFulfillable)} of ${fmtQty(combined.totalRequired)} — the order can be fully fulfilled.`
              : `Combined across all warehouses: ${fmtQty(combined.totalFulfillable)} of ${fmtQty(combined.totalRequired)} — short ${fmtQty(combined.totalShort)}. Not enough stock anywhere.`} />

          <Table size="small" loading={isFetching} pagination={false} rowKey="warehouseId" dataSource={data.warehouses ?? []}
            rowSelection={canPlan ? { type: 'radio', selectedRowKeys: selected ? [selected] : [], onChange: (k) => setPicked(String(k[0])) } : undefined}
            rowClassName={(w: any) => (w.coverage === 'FULL' ? 'ant-table-row-selected' : '')}
            columns={[
              { title: 'Warehouse', render: (_: any, w: any) => (<div><b>{w.name}</b> <span style={{ color: '#999' }}>{w.code ? `(${w.code})` : ''}</span><div>{flags(w)}</div></div>) },
              { title: 'Coverage', align: 'center' as const, render: (_: any, w: any) => <Tag color={COVERAGE[w.coverage]?.color}>{COVERAGE[w.coverage]?.label ?? w.coverage}</Tag> },
              { title: 'Fulfillable / Required', align: 'right' as const, render: (_: any, w: any) => `${fmtQty(w.totalFulfillable)} / ${fmtQty(w.totalRequired)}` },
              { title: 'Short', align: 'right' as const, render: (_: any, w: any) => (w.totalShort > 0 ? <b style={{ color: '#d4380d' }}>{fmtQty(w.totalShort)}</b> : '—') },
            ]}
            expandable={{
              rowExpandable: (w: any) => (w.lines?.length ?? 0) > 0,
              expandedRowRender: (w: any) => (
                <Table size="small" pagination={false} rowKey="productId" dataSource={w.lines}
                  columns={[
                    { title: 'Product', render: (_: any, l: any) => l.productName ?? l.productId },
                    { title: 'Required', align: 'right' as const, render: (_: any, l: any) => fmtQty(l.requiredQty) },
                    { title: 'Available here', align: 'right' as const, render: (_: any, l: any) => <span style={{ color: l.shortQty > 0 ? '#d4380d' : undefined }}>{fmtQty(l.availableQty)}</span> },
                    { title: 'Short', align: 'right' as const, render: (_: any, l: any) => (l.shortQty > 0 ? <b style={{ color: '#d4380d' }}>{fmtQty(l.shortQty)}</b> : '—') },
                  ]} />
              ),
            }} />

          {/* Transfer suggestion (NEEDS_TRANSFER) */}
          {outcome === 'NEEDS_TRANSFER' && (
            <div style={{ marginTop: 12 }}>
              {s.transferPlanError ? (
                <Alert type="warning" title="Transfer plan could not be generated; a planner must review manually." description={s.transferPlanError} />
              ) : (s.transferPlan?.length ? (
                <Alert type="warning"
                  title={`No single warehouse covers this order, but the combined stock does — transfer the shortfall into ${selectedRow?.name ?? 'the destination'} and ship as one.`}
                  description={<div>{s.transferPlan.map((p: any) => (
                    <div key={p.fromWarehouseId} style={{ fontSize: 13 }}>• <b>{p.fromName}</b> → {selectedRow?.name}: {p.lines.map((l: any) => `${l.productName ?? l.productId} ×${fmtQty(l.qty)}`).join(', ')}</div>
                  ))}</div>} />
              ) : null)}
            </div>
          )}

          {outcome === 'INSUFFICIENT_TOTAL' && (
            <Alert style={{ marginTop: 12 }} type="error"
              title={`Not enough stock across all warehouses (short ${fmtQty(combined.totalShort)}).`}
              description="Options: add stock (GRN/production), assign a warehouse and dispatch what's available now (partial), or assign and wait for full stock." />
          )}

          {/* Actions — permission-gated; backend is the final authority */}
          <div style={{ marginTop: 14 }}>
            {canPlan ? (
              <Space wrap>
                {selectedRow?.coverage === 'FULL' && (
                  <Button variant="primary" loading={assign.isPending} disabled={selectedRow.isAssigned}
                    onClick={() => assign.mutate(selected!)}>
                    {selectedRow.isAssigned ? 'Assigned (covers fully)' : `Assign ${selectedRow.name}`}
                  </Button>
                )}
                {outcome === 'NEEDS_TRANSFER' && selected && (
                  <Popconfirm title="Consolidate & hold?" description="Transfers the shortfall in; the order is held until they are received."
                    okText="Consolidate" onConfirm={() => consolidate.mutate(selected)} disabled={!!s.transferPlanError || !(s.transferPlan?.length)}>
                    <Button variant="primary" icon={undefined} loading={consolidate.isPending} disabled={!!s.transferPlanError || !(s.transferPlan?.length)}>
                      Consolidate &amp; hold order
                    </Button>
                  </Popconfirm>
                )}
                {selected && selectedRow?.coverage !== 'FULL' && (
                  <>
                    <Tooltip label="Assign this warehouse now; then use Pick &amp; pack to ship what's available. The rest stays pending.">
                      <Button variant="default" loading={assign.isPending} disabled={selectedRow?.isAssigned} onClick={() => assign.mutate(selected)}>Assign &amp; pack available now</Button>
                    </Tooltip>
                    <Tooltip label="Assign this warehouse and wait for stock to arrive (transfer / production) before packing.">
                      <Button variant="default" loading={assign.isPending} disabled={selectedRow?.isAssigned} onClick={() => assign.mutate(selected)}>Assign &amp; wait for stock</Button>
                    </Tooltip>
                  </>
                )}
                {canAddStock && !combined.canFulfill && (
                  <Tooltip label="Add opening stock at the master warehouse (a goods-receipt note is created automatically), then transfer it out.">
                    <Button variant="default" onClick={() => navigate('/inventory/master-stock')}>Add stock</Button>
                  </Tooltip>
                )}
              </Space>
            ) : (
              <Typography.Text type="secondary">You can view coverage. A planner (dispatch/edit permission) assigns the warehouse or arranges a transfer.</Typography.Text>
            )}
            {/* Phase 2 — a packer (WHM) who can't consolidate escalates the shortfall to the planner. */}
            {canReportShortfall && isShort && (
              <div style={{ marginTop: 8 }}>
                <Button variant="danger" onClick={() => setReportOpen(true)}>Report shortfall to planner</Button>
              </div>
            )}
          </div>
        </>
      )}

      <Modal
        open={reportOpen}
        title="Report shortfall to planner"
        onOpenChange={(o) => { if (!o) setReportOpen(false); }}
        footer={<>
          <Button variant="default" onClick={() => setReportOpen(false)}>Cancel</Button>
          <Button variant="primary" loading={reportShortfall.isPending} onClick={() => reportShortfall.mutate()}>Report shortfall</Button>
        </>}
      >
        <Typography.Paragraph type="secondary">
          This alerts Admin/planners that this order is short at the assigned warehouse so they can arrange a
          transfer or add stock. The short quantities are taken from live coverage — you don't enter them.
        </Typography.Paragraph>
        <Input.TextArea rows={3} maxLength={500} placeholder="Optional remarks for the planner (e.g. urgency, customer commitment)…"
          value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </Modal>
    </Card>
  );
}
